import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import UVMBannerImage from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./UVM.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const UVM = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowThankYou(false);
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    overview: useRef(null),
    baseClasses: useRef(null),
    components: useRef(null),
    sequences: useRef(null),
    envConfig: useRef(null),
    factory: useRef(null),
    phases: useRef(null),
    reports: useRef(null),
    callbacks: useRef(null),
    challenges: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // scroll to first match on search
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in sections when visible
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        e.isIntersecting
          ? e.target.classList.add("visible")
          : e.target.classList.remove("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto‐dismiss thank you popup
  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page uvm">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Universal Verification Methodology (UVM)</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | UVM
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    overview: "UVM Overview",
                    baseClasses: "Base Classes",
                    components: "Core Components",
                    sequences: "Sequences & Drivers",
                    envConfig: "Environment & Config",
                    factory: "Factory & Overrides",
                    phases: "Execution Phases",
                    reports: "Reporting & Messaging",
                    callbacks: "Callbacks & Extensions",
                    challenges: "Challenges & Tips",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={UVMBannerImage}
            alt="UVM Banner"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Alice Verification</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              3 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction</h2>
          <p className="fade-section">
            <Highlight
              text="UVM is an IEEE-standardized SystemVerilog class library and methodology that provides a uniform, scalable framework for functional verification."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="It builds upon the e language’s OVM and VMM, offering reusable components, configuration, reporting, and a well-defined execution flow."
              query={searchTerm}
            />
          </p>

          {/* 2. UVM Overview */}
          <h2 ref={sectionRefs.overview} className="fade-section">UVM Overview</h2>
          <p className="fade-section">
            <Highlight
              text="At its core, UVM defines base classes such as uvm_object, uvm_component, uvm_sequence, and uvm_sequence_item that you extend to build your testbench."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="It enforces a layered architecture: test → env → agent → driver/monitor → DUT, enabling modularity and parallel development."
              query={searchTerm}
            />
          </p>

          {/* 3. Base Classes */}
          <h2 ref={sectionRefs.baseClasses} className="fade-section">Base Classes</h2>
          <p className="fade-section">
            <Highlight
              text="uvm_object is the ancestor for all transaction-level elements, providing factory registration and copy/cloning capabilities."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="uvm_component extends uvm_object with hierarchical naming, configuration database access, and built-in reporting."
              query={searchTerm}
            />
          </p>

          {/* 4. Core Components */}
          <h2 ref={sectionRefs.components} className="fade-section">Core Components</h2>
          <p className="fade-section">
            <Highlight
              text="uvm_driver drives DUT interfaces based on sequence items, while uvm_monitor observes DUT signals and converts them back to sequence items."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="uvm_scoreboard compares actual vs expected results, often relying on a golden reference model for verification."
              query={searchTerm}
            />
          </p>

          {/* 5. Sequences & Drivers */}
          <h2 ref={sectionRefs.sequences} className="fade-section">Sequences & Drivers</h2>
          <p className="fade-section">
            <Highlight
              text="uvm_sequence generates transaction-level stimulus. You can compose sequences hierarchically and randomize sequence_items with constraints."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Drivers fetch items from the sequencer and translate them into pin-level stimulus for the DUT."
              query={searchTerm}
            />
          </p>

          {/* 6. Environment & Configuration */}
          <h2 ref={sectionRefs.envConfig} className="fade-section">Environment & Configuration</h2>
          <p className="fade-section">
            <Highlight
              text="uvm_env aggregates agents and components under a single container, providing a centralized hook for configuration."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="The UVM configuration database (uvm_config_db) allows test-specific parameters to be set in the test and accessed deep in the component hierarchy without tight coupling."
              query={searchTerm}
            />
          </p>

          {/* 7. Factory & Overrides */}
          <h2 ref={sectionRefs.factory} className="fade-section">Factory & Overrides</h2>
          <p className="fade-section">
            <Highlight
              text="The UVM factory enables creation of generic objects and components via type strings, allowing tests to override implementations without code changes."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use uvm_set_type_override and uvm_set_inst_override to substitute stub or extended components at runtime."
              query={searchTerm}
            />
          </p>

          {/* 8. Execution Phases */}
          <h2 ref={sectionRefs.phases} className="fade-section">Execution Phases</h2>
          <p className="fade-section">
            <Highlight
              text="UVM defines a standardized sequence of phases—build, connect, end_of_elaboration, start_of_simulation, run, extract, report—ensuring consistent testbench behavior."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Override or extend phases in your component’s build_phase(), run_phase(), and report_phase() methods for custom behavior."
              query={searchTerm}
            />
          </p>

          {/* 9. Reporting & Messaging */}
          <h2 ref={sectionRefs.reports} className="fade-section">Reporting & Messaging</h2>
          <p className="fade-section">
            <Highlight
              text="uvm_report_* macros (INFO, WARNING, ERROR, FATAL) integrate with UVM’s verbosity control and severity thresholds."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Configure verbosity and action on messages via command-line options or uvm_set_report_verbosity_level."
              query={searchTerm}
            />
          </p>

          {/* 10. Callbacks & Extensions */}
          <h2 ref={sectionRefs.callbacks} className="fade-section">Callbacks & Extensions</h2>
          <p className="fade-section">
            <Highlight
              text="UVM callbacks let you inject custom behavior—logging, coverage hooks, or alternate stimulus—without modifying the original component code."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Register callbacks with uvm_callback_registry and implement methods like pre_reset(), post_write(), etc."
              query={searchTerm}
            />
          </p>

          {/* 11. Challenges & Tips */}
          <h2 ref={sectionRefs.challenges} className="fade-section">Challenges & Tips</h2>
          <p className="fade-section">
            <Highlight
              text="UVM has a learning curve: start small with standalone components, then incrementally integrate into larger environments."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use built-in UVM examples (uvm/examples/) as templates. Leverage code generation scripts to avoid boilerplate."
              query={searchTerm}
            />
          </p>

          {/* 12. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends</h2>
          <p className="fade-section">
            <Highlight
              text="Portable Stimulus Standard (PSS) integration will complement UVM for system-level reuse."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Machine-learning driven coverage analysis and stimulus generation promises to accelerate closure of hard-to-reach corner cases."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>3 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">UVM Fan</span>
                    <span className="comment-date">May 8, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Fantastic deep dive on phases—helped me debug my build_phase override!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Sequence Master</span>
                    <span className="comment-date">May 9, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                The section on factory overrides saved me hours—clear and practical!
              </p>
              <button className="reply-button" onClick={() => toggleReply("seqmaster")}>
                Reply
              </button>
              <AnimatePresence initial={false}>
                {replyVisible["seqmaster"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to Sequence Master</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">Reply</button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Comment 3 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Config Guru</span>
                    <span className="comment-date">May 10, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great tips on using uvm_config_db—really untangled my test parameter craziness!
              </p>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here…" required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      overview: "UVM Overview",
                      baseClasses: "Base Classes",
                      components: "Core Components",
                      sequences: "Sequences & Drivers",
                      envConfig: "Environment & Config",
                      factory: "Factory & Overrides",
                      phases: "Execution Phases",
                      reports: "Reporting & Messaging",
                      callbacks: "Callbacks & Extensions",
                      challenges: "Challenges & Tips",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UVM;